import * as moment from 'moment';
import * as _ from 'lodash';
import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './styles';
import { CellFocusedEvent, ColDef, ColSpanParams, ColumnMovedEvent, ColumnResizedEvent, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowClassParams, RowClickedEvent, RowDataChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { FormControl } from 'react-bootstrap';
import { DisplayType, TicketEntityType, TicketStatus, TicketType, UserGroup } from '@nkr/enums/index';
import { IGroup, IListItem, ITicketForList, ITicketGroup, IUser } from '@nkr/models/index';

interface IAgentRendererParams extends ICellRendererParams { userGroupList: IGroup[], onChangeAssignedUser: (ticketId: number, userId: number) => void };
interface IStatusRendererParams extends ICellRendererParams { statusList: IListItem[], onChangeStatus: (ticketId: number, status: number) => void };
interface IGroupRendererParams extends ICellRendererParams { groupList: ITicketGroup[], onChangeGroup: (ticketId: number, groupId: number) => void };

const disableRowSelectionColumns = ['Agent', 'Status', 'Group'];

interface IProps {
    data: ITicketForList[],
    display: DisplayType,
    userList: IUser[],
    userGroupList: IGroup[],
    statusList: IListItem[],
    groupList: ITicketGroup[],
    onGridReady: (e: GridReadyEvent) => void,
    onRowClicked: (e: RowClickedEvent) => void,
    onColumnResized: (e: ColumnResizedEvent) => void,
    onColumnMoved: (event: ColumnMovedEvent) => void,
    onDragStopped: () => void,
    onChangeAssignedUser: (ticketId: number, userId: number) => void,
    onChangeStatus: (ticketId: number, status: number) => void,
    onChangeGroup: (ticketId: number, groupId: number) => void,
}

const AdminGrid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
    }

    const getRowStyle = (params: RowClassParams) => {
        let data = params.data as ITicketForList;

        if (data.ticketStatus == TicketStatus.Closed || data.ticketStatus == TicketStatus.Resolved) {
            return { color: '#9C9EA3' };
        }

        if (data.entityType == TicketEntityType.Center || data.ticketType == TicketType.ContactPageNonMemberCenter || data.ticketType == TicketType.MemberCenterRequest || data.isCenter == true) {
            return { backgroundColor: 'yellow' };
        }
    };

    const cols: ColDef[] = [
        {
            colId: 'TicketNumber',
            field: 'id',
            headerName: 'Ticket #',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Ticket #</SortableHeaderComponent>,
            width: 75,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
        },
        {
            colId: 'FromSubject',
            headerName: 'From and Subject',
            headerClass: 'list-header',
            headerComponentFramework: () => <span style={{ cursor: 'pointer' }}>From &amp; Subject</span>,
            width: 400,
            cellClass: 'list-cell-left',
            sortable: false,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITicketForList;
                let { contactName, contactEmail, isMissingData } = data;

                if (isMissingData == false) {
                    return (
                        <div style={{ textAlign: 'left', width: '100%' }}>
                            <div style={{ lineHeight: 'initial' }}>{contactEmail ? `${contactName} (${contactEmail})` : contactName}</div>
                            <div style={{ lineHeight: 'initial' }}>subject: {data.subject ? data.subject : '[No Subject]'}</div>
                        </div>
                    );
                }

                if (isMissingData)
                    return (
                        <div className='bg-danger' style={{ padding: 5, width: '100%' }}>
                            There is an issue with this ticket, please alert the developers...
                        </div>
                    );

                return null;
            },
            colSpan: (params: ColSpanParams) => {
                let data = params.data as ITicketForList;
                let { isMissingData } = data;

                if (isMissingData) {
                    return 8;
                }

                return 1;
            }
        },
        {
            colId: 'Agent',
            field: 'assignedUserId',
            headerName: 'Agent',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Agent</SortableHeaderComponent>,
            width: 225,
            cellClass: 'list-cell-dropdown',
            sortable: true,
            resizable: true,
            cellRendererParams: { userList: props.userList, userGroupList: props.userGroupList, onChangeAssignedUser: props.onChangeAssignedUser },
            cellRendererFramework: (params: IAgentRendererParams) =>
                <>
                    <FormControl
                        componentClass='select'
                        bsSize='sm'
                        style={{ color: params.data.ticketStatusId == TicketStatus.Closed || params.data.ticketStatusId == TicketStatus.Resolved ? '#bbb' : '#555' }}
                        value={params.data.assignedUserId}
                        onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onChangeAssignedUser(params.data.id, parseInt(e.currentTarget.value))}
                    >
                        <option value={0}>- PENDING ASSIGNMENT -</option>
                        {params.userGroupList.map(group => {
                            let userList = props.userList.filter(x => x.groupId == group.id);
                            let sortedUserList = _.sortBy(userList, [item => item.firstName]);

                            return (
                                <optgroup key={'group-' + group.id} label={group.id == UserGroup.Admin ? 'NKR' : group.name}>
                                    {sortedUserList.map((item) =>
                                        <option key={`assigned-user-${item.id}`} value={item.id}>{item.firstName} {item.lastName}</option>
                                    )}
                                </optgroup>
                            );
                        })}
                    </FormControl>
                </>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITicketForList;
                let user = props.userList.find(x => x.id == data.assignedUserId);
                return user != null ? user.firstName + ' ' + user.lastName : 'z';
            }
        },
        {
            colId: 'RequestType',
            field: 'typeDescription',
            headerName: 'Request Type',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Request Type</SortableHeaderComponent>,
            width: 200,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) =>
                <>
                    {params.value ? params.value : <span style={{ fontStyle: 'italic' }}>[Not Specified]</span>}
                </>
        },

        {
            colId: 'Center',
            field: 'centerAlias',
            headerName: 'Center',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
        },

        {
            colId: 'Status',
            field: 'ticketStatus',
            headerName: 'Status',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            width: 150,
            cellClass: 'list-cell-dropdown',
            sortable: true,
            resizable: true,
            cellRendererParams: { statusList: props.statusList, onChangeStatus: props.onChangeStatus },
            cellRendererFramework: (params: IStatusRendererParams) => {
                let data = params.data as ITicketForList;

                return (
                    <>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            style={{ color: params.data.ticketStatusId == TicketStatus.Closed || params.data.ticketStatusId == TicketStatus.Resolved ? '#bbb' : '#555' }}
                            value={params.value}
                            onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onChangeStatus(params.data.id, parseInt(e.currentTarget.value))}
                        >
                            {params.statusList.map((item) =>
                                <option key={`status-${item.id}`} value={item.id}>{item.description}</option>
                            )}
                        </FormControl>
                    </>
                );
            }
        },

        {
            colId: 'Tier',
            field: 'tierDescription',
            headerName: 'Tier',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Tier</SortableHeaderComponent>,
            width: 80,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) =>
                <>
                    {params.value ? params.value : <span style={{ fontStyle: 'italic' }}>[Not Specified]</span>}
                </>
        },

        {
            colId: 'createdDateTime',
            field: 'createdDateTime',
            headerName: 'Submitted',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Created Date</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) => <>{moment(params.data.createdDateTime).format('MM/DD/YYYY HH:mm')}</>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITicketForList;
                return new Date(data.createdDateTime);
            }
        },

        {
            colId: 'lastUpdatedDateTime',
            field: 'lastUpdatedDateTime',
            headerName: 'Submitted',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Updated</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) => <>{moment(params.data.lastUpdatedDateTime).format('MM/DD/YYYY HH:mm')}</>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITicketForList;
                return new Date(data.lastUpdatedDateTime);
            }
        },

        {
            colId: 'closedDateTime',
            field: 'closedDateTime',
            headerName: 'Closed',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Closed</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value != null ? moment(params.value).format('MM/DD/YYYY HH:mm') : '--'}</>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITicketForList;

                if (data.closedDateTime != null) {
                    return new Date(data.closedDateTime);
                }

                return null;
            }
        },

        {
            colId: 'Urgent',
            field: 'urgencyId',
            headerName: 'Urgent',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Urgent</SortableHeaderComponent>,
            width: 100,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRenderer: (params: any) => parseInt(params.value) != 0 ? 'Yes' : 'No'
        },

        {
            colId: 'Group',
            field: 'groupName',
            headerName: 'Group',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Group</SortableHeaderComponent>,
            width: 175,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererParams: { groupList: props.groupList, onChangeGroup: props.onChangeGroup },
            cellRendererFramework: (params: IGroupRendererParams) => {
                let data = params.data as ITicketForList;

                return (
                    <>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            style={{ color: params.data.ticketStatusId == TicketStatus.Closed || params.data.ticketStatusId == TicketStatus.Resolved ? '#bbb' : '#555' }}
                            value={data.ticketGroupId}
                            onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onChangeGroup(params.data.id, parseInt(e.currentTarget.value))}
                        >
                            {params.groupList.map((item) =>
                                <option key={`grp-${item.id}`} value={item.id}>{item.name}</option>
                            )}
                        </FormControl>
                    </>
                );
            }
        }
    ];

    const columnDefs = useMemo(() => cols, [props.statusList, props.groupList, props.userList]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper style={{ width: '100%', height: props.display != DisplayType.NoMenuNoTabs ? '64vh' : '84vh' }} fontSize='12px' className='ag-theme-bootstrap' >
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                rowHeight={38}
                immutableData={true}
                suppressNoRowsOverlay={true}
                suppressCellSelection={true}
                suppressContextMenu={true}
                getRowStyle={getRowStyle}
                getRowNodeId={(data: ITicketForList) => data.id.toString()}
                onGridReady={onGridReady}
                onRowClicked={props.onRowClicked}
                onColumnResized={props.onColumnResized}
                onColumnMoved={props.onColumnMoved}
                onDragStopped={props.onDragStopped}
                onCellFocused={(e: CellFocusedEvent) => {
                    if (e.column && disableRowSelectionColumns.indexOf(e.column.getColDef().colId) >= 0) {
                        (e.api as any).gridOptionsWrapper.gridOptions.suppressRowClickSelection = true;
                    } else {
                        (e.api as any).gridOptionsWrapper.gridOptions.suppressRowClickSelection = false;
                    }
                }}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => {
                    e.api.sizeColumnsToFit();
                }}
                onRowDataChanged={(e: RowDataChangedEvent) => {
                    e.api.sizeColumnsToFit();
                }}
            />
        </GridWrapper >
    );
}

export default AdminGrid;